import React, { Fragment, useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Label, Input } from "reactstrap";
import ThumbnailNotAvailable from '../Project/assets/material/thumbnail_not_available.svg';
import { SignContract } from './Blockchain/SignContract';
import { SolutionConstants } from '../../constants/solution';
import { APIConstants } from '../../constants/api';
import useSolution from '../../hooks/useSolution';
import useUser from '../../hooks/useUser';
import useBlobStorage from '../../hooks/useBlobStorage';
import FormToast from '../Form/Toast';
import Bathroom from '../Project/assets/spaces/bathroom.svg';
import Kitchen from '../Project/assets/spaces/kitchen.svg';
import Living from '../Project/assets/spaces/living.svg';
import ClockCountDown from './assets/clock_count_down.svg';
import { ActionConfirm } from './Blockchain/ActionConfirm';
import { EscrowSmartContract } from './Details/EscrowSmartContract';

function SolutionDetails({ solutions, args, onSolutionUpdated, userType }) {
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [contractDeployed, setContractDeployed] = useState(false);
    const [selectedUser, setSelectedUser] = useState('');
    const [forceUpdateSolutions, setForceUpdateSolutions] = useState(false);
    const [showCreateContractNotice, setShowCreateContractNotice] = useState(false);
    const [showDeployContractNotice, setShowDeployContractNotice] = useState(false);
    const contractCreateErrorRef = useRef();
    const currentSolution = useRef()
    const params = useParams();
    const navigate = useNavigate();
    const solutionHelper = useSolution();
    const solutionContractor = useSolution();
    const solutionManager = useSolution();
    const userHelper = useUser();
    const blobStorage = useBlobStorage();
    const isContractor = userType === 'contractor';

    const ETHERSCAN_ADDRESS = 'https://sepolia.etherscan.io/address/';

    const spaceAssets = {
        'Bathroom': Bathroom,
        'Kitchen': Kitchen,
        'Living': Living
    }

    const toggle = () => {
        setShowModal(!showModal);
        setSelectedUser('');
        if (showModal) {
            if (typeof onSolutionUpdated === 'function') {
                if (solutionUpdated() || forceUpdateSolutions) {
                    onSolutionUpdated(solutionHelper.solution);
                }
            }

            navigate(-1);
        }
    };

    const toggleImagePreview = (previewUrl) => {
        setImagePreview(previewUrl);
    };

    useEffect(() => {
        const modalData = getModalData();
        setModalData(getModalData());
        setShowModal(modalData !== null);
        
        if (modalData?.id) {
            solutionManager.getById(modalData?.id);
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params, solutions]);

    useEffect(() => {
        if(solutionHelper.solution) {
            currentSolution.current = solutionHelper.solution;
            setContractDeployed(!contractDeployed && !!solutionHelper.solution.escrowSmartContract?.address);
            if (solutionHelper.solution.id) {
                solutionManager.getById(solutionHelper.solution.id);
            }
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [solutionHelper.solution]);

    useEffect(() => {
        if (solutionContractor.solution) {
            currentSolution.current = solutionContractor.solution;
            if (solutionContractor.solution.id) {
                solutionManager.getById(solutionContractor.solution.id);
            }
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [solutionContractor.solution]);

    useEffect(() => {
        userHelper.getContractorList();
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getModalData = () => {
        let data = null;

        if (params && params['*']) {
            let solution = getSolution();
            if (solution && solution.id) {
                let location = solution.location;
                let project = solution.projects?.length ? solution.projects[0] : null;

                data = {
                    id: solution.id,
                    address: location.addressLine,
                    postcode: location.postalCode,
                    administrativeArea: location.administrativeAreaName,
                    contractorAccountAddress: solution.contractorAccountAddress,
                    homeOwnerAccountAddress: solution.homeOwnerAccountAddress,
                    totalBudget: solution.totalBudget,
                    smartContractAddress: solution.smartContractAddress,
                    escrowSmartContract: solution.escrowSmartContract,
                    currency: solution.currency,
                    laborBudget: solution.laborBudget,
                    projectType: project ? project.projectType : null,
                    projectDescription: project ? project.description : null,
                    materials: project ? project.projectMaterialDetails : null,
                    mediaFiles: project ? project.projectMediaFiles: []
                }
            }
        }

        return data;
    }

    const getSolution = (id) => {
        let solution = {};

        if (!currentSolution.current) {
            if (solutions && solutions.length > 0) {
                if (!id && params['*']) {
                    id = params['*'];
                }
                solution = solutions.find(solution => solution.id === id);
            }
        } else {
            solution = currentSolution.current;
            // TODO: Avoid this Normalization
            if (typeof solution.escrowSmartContract === 'object'
                && solution.escrowSmartContract !== null
            ) {
                solution.escrowSmartContract = solution.escrowSmartContract?.address;
            }
            // ----------
        }
        
        return solution;
    }

    const solutionUpdated = () => {
        return solutionHelper.solution !== null || solutionHelper.error
    }

    const EtherLogo = ({ style, className }) => {
        return (
            <svg style={style} version="1.1" className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 327.5 533.3">
                <path className="st0" d="M163.7,197.2V0L0,271.6L163.7,197.2z" fill="#8A92B2" />
                <path className="st1" d="M163.7,368.4V197.2L0,271.6L163.7,368.4z M163.7,197.2l163.7,74.4L163.7,0V197.2z" fill="#62688F" />
                <path className="st2" d="M163.7,197.2v171.2l163.7-96.8L163.7,197.2z" fill="#454A75" />
                <path className="st0" d="M163.7,399.4L0,302.7l163.7,230.7V399.4z" />
                <path className="st1" d="M327.5,302.7l-163.8,96.7v134L327.5,302.7z" />
            </svg>
        )
    }

    const Contract = () => {
        let solution = getSolution();
        if (solutionHelper.solution !== null && !solutionHelper.error) {
            // TODO: Avoid this Normalization
            if (typeof solutionHelper.solution.escrowSmartContract === 'object'
                && solutionHelper.solution.escrowSmartContract !== null
            ) {
                solutionHelper.solution.escrowSmartContract = solutionHelper.solution.escrowSmartContract?.address;
            }
            // ----------
            return (
                <DeployContract solution={solutionHelper.solution} />
            )
        } else {
            if (solution) {
                if (!solution.escrowSmartContract) {
                    if (isContractor) {
                        if (solution.stateAssignment.state.code !== SolutionConstants.STATE.CONTRACTOR_REJECTED) {
                            return (
                                <>
                                    {
                                        solution.stateAssignment.state.code === SolutionConstants.STATE.CONTRACTOR_ASSIGNED &&
                                        <p className='d-inline'>
                                            <span className={'text-success'}>● </span>
                                            The Homeowner is waiting for you to accept or decline this project.
                                        </p>
                                    }
                                    {
                                        solution.stateAssignment.state.code === SolutionConstants.STATE.CONTRACTOR_CONFIRMED &&
                                        <>
                                            <div className='row mt-3'>
                                                <div className='col-sm-12'>
                                                    Homeowner contact Info
                                                </div>
                                            </div>
                                            <div className='row w-100 mt-1'>
                                                <div className='col-3'>
                                                    Name:
                                                </div>
                                                <div className='col-9'>
                                                    {`${solution.homeowner?.firstName} ${solution.homeowner?.lastName}`}
                                                </div>
                                            </div>
                                            <div className='row w-100'>
                                                <div className='col-3'>
                                                    Email:
                                                </div>
                                                <div className='col-9'>
                                                    <a className='email text-decoration-none' href={`mailto: ${solution.homeowner?.email}`}>
                                                        {solution.homeowner?.email}
                                                    </a>
                                                </div>
                                            </div>
                                            {
                                                solution.homeowner?.phone &&
                                                <div className='row w-100'>
                                                    <div className='col-3'>
                                                        Phone:
                                                    </div>
                                                    <div className='col-9'>
                                                        <a className='phone text-decoration-none' href={`tel: ${solution.homeowner?.phone}`}>
                                                            {solution.homeowner?.phone}
                                                        </a>
                                                    </div>
                                                </div>
                                            }
                                        </>
                                    }
                                    {
                                        solution.stateAssignment.state.code === SolutionConstants.STATE.CONTRACT_CREATED &&
                                        <>
                                            The Homeowner created the Contract.
                                        </>
                                    }
                                </>
                            );
                        } else {
                            return (
                                <>
                                    <p style={{ width: '500px' }}>
                                        <span className={'text-danger'}>● </span>
                                        This project was declined
                                    </p>
                                    {
                                        solution.stateAssignment.comment &&
                                        <textarea className='d-block w-100' defaultValue={solution.stateAssignment.comment}/>
                                    }
                                </>
                            );
                        }
                    }
                    return (
                        solution.stateAssignment.state.code !== SolutionConstants.STATE.CONTRACT_CREATED ? (
                        <CreateContract />
                        ) : (
                            <DeployContract solution={solution} />
                        )
                    );
                } else {
                    return (
                        <DeployContract solution={solution} />
                    );
                }
            }
        }
    }

    const userListOptions = userHelper.userList?.map(
        (user) => <option key={user.id} value={user.id} disabled={!user.accountAddress}>
            {user.firstName + ' ' + user.lastName + (!user.accountAddress ? ' (No Address)' : '')}
        </option>
    );

    const handleSelectedUserChange = (e) => {
        setSelectedUser(e.target.value);        
    }

    const handleContractorChooose = () => {
        let solution = getSolution();
        
        if (solution && solution.id && selectedUser) {
            solutionContractor.setContractor (solution.id, selectedUser);
        }
    }

    const CreateContract = () => {
        let solution = solutionContractor.solution || getSolution();
        return (
            <Fragment>
                <div className='row mt-3'>
                    <div className='col-sm-12'>
                        {
                            solution?.stateAssignment.state.code !== SolutionConstants.STATE.CONTRACTOR_REJECTED ? (
                                <>
                                    {
                                        solution?.contractor === null ? (
                                            <p className='d-inline'>
                                                You need to choose a Contractor to create the contract
                                            </p>
                                        ) : (
                                                solution?.stateAssignment.state.code === SolutionConstants.STATE.CONTRACTOR_CONFIRMED ? (
                                                    <p className='d-inline'>Ready to create contract</p>
                                                ) : (
                                                    solution?.stateAssignment.state.code === SolutionConstants.STATE.CONTRACT_CREATED ? (
                                                        <p className='d-inline'>Contract created</p>
                                                    ) : (
                                                        <Button color='dark' className='w-100 rounded-3' size='lg' active={false} disabled={true}>
                                                            <img className='mt-1 me-2'
                                                                style={{ verticalAlign: 'sub' }}
                                                                alt=''
                                                                src={ClockCountDown}
                                                            ></img>
                                                            Waiting for contractor to accept
                                                        </Button>
                                                    )
                                                )
                                        )
                                    }
                                </>
                            ) : (
                                <>
                                    <span className="text-warning">● </span>
                                    <p className='d-inline'>The contractor declined this request</p>
                                    {
                                        solution.stateAssignment.comment &&
                                        <>
                                            <p>Comments:</p>
                                            <textarea className='d-block w-100' defaultValue={solution.stateAssignment.comment} />
                                        </>
                                    }
                                </>
                            )
                        }
                    </div>
                </div>
                {
                    solution?.contractor === null &&
                        solution?.stateAssignment.state.code !== SolutionConstants.STATE.CONTRACTOR_REJECTED &&
                    <Fragment>
                        <div className='row mt-2'>
                            <div className='col-sm-10'>
                                <Input type="select"
                                    value={selectedUser}
                                    onChange={handleSelectedUserChange}
                                    disabled={userListOptions?.length === 0}>
                                    <option value="" disabled hidden>
                                        Choose a Contractor...
                                    </option>
                                    {userListOptions ?? []}
                                </Input>
                            </div>
                            <div className='col-sm-2 text-end'>
                                <Button color="success" disabled={!selectedUser || solutionContractor?.loading} onClick={handleContractorChooose}>
                                    Choose
                                </Button>
                                {
                                    solutionContractor?.loading &&
                                    <div className="upload-spinner d-inline ms-1">
                                        <div role="status" className="spinner-border spinner-border-sm text-success align-middle">
                                            <span className="sr-only"></span>
                                        </div>
                                    </div>
                                }
                                
                            </div>
                        </div>
                    </Fragment>
                }                
                
                {
                    solution?.contractor !== null &&
                        solution?.stateAssignment.state.code === SolutionConstants.STATE.CONTRACTOR_CONFIRMED &&
                    <Fragment>
                        <div className='row mt-2'>
                            <div className='col-sm-4'>
                                <Label className="mb-0">Contractor Name</Label>
                            </div>
                            <div className='col-sm-8'>
                                <input type="text"
                                    className="border w-100"
                                    defaultValue={`${solution.contractor?.firstName} ${solution.contractor?.lastName}`}
                                />
                            </div>
                        </div>
                        <div className='row mt-2'>
                            <div className='col-sm-4'>
                                <Label className="mb-0">Contractor Email Address</Label>
                            </div>
                            <div className='col-sm-8'>
                                <a className='email' href={`mailto: ${solution.contractor?.email}`}>
                                    {solution.contractor?.email}
                                </a>
                            </div>
                        </div>
                        {
                            solution.contractor?.phone &&
                                <div className='row mt-2'>
                                    <div className='col-sm-4'>
                                        <Label className="mb-0">Contractor Phone</Label>
                                    </div>
                                    <div className='col-sm-8'>
                                        <a className='phone' href={`tel: ${solution.contractor?.phone}`}>
                                            {solution.contractor?.phone}
                                        </a>
                                    </div>
                                </div>
                        }
                        <div className='row mt-2'>
                            <label className='text-danger' ref={contractCreateErrorRef}></label>
                        </div>
                        <div className='row mt-3'>
                            <div className='col-sm-12'>
                                <Button
                                    color="dark"
                                    className="w-100 p-3 rounded-3"
                                    disabled={solutionHelper.loading}
                                    onClick={() => {setShowCreateContractNotice(true)}}
                                >
                                    Create Contract
                                </Button>
                                <div className={solutionHelper.loading ? "upload-spinner d-inline ms-1" : "visually-hidden"}>
                                    <div role="status" className="spinner-border spinner-border-sm text-success align-middle">
                                        <span className="sr-only"></span>
                                    </div>
                                </div>
                                <label className='mt-1' style={{fontFamily: 'inter'}}>
                                    You will have an opportunity to cancel in the next step.
                                </label>
                            </div>
                        </div>
                        <ActionConfirm
                            key={'createConfirm'}
                            visible={showCreateContractNotice}
                            message={
                                'The Builde App automatically calculates the value of the Scope ' +
                                'of Work in Ether and generates a PDF contract. ' +
                                'This contract is then emailed to both the Homeowner and the Contractor for their records.'
                            }
                            onConfirm={() => {createContract()}}
                            onClose={() => {setShowCreateContractNotice(false)}}
                        />
                    </Fragment>
                }
            </Fragment>
        );
    }

    const ContractView = ({ solution }) => {
        return (
            <Fragment>
                <div className='row mt-3'>
                    <div className='col-sm-4'>
                        <p>Your contract address</p>
                    </div>
                    <div className='col-sm-8'>
                        <p className='mb-0'>
                            <span className='text-info'>
                                <a className='text-info' target="_blank" rel="noreferrer" href={ETHERSCAN_ADDRESS + solution.escrowSmartContract}>
                                    {solution.escrowSmartContract}
                                </a>
                            </span>
                        </p>
                        <small>Click to view on Etherscan</small>
                    </div>
                </div>
                <hr />
                <div className='mt-3'>
                    {
                        showModal ? (
                            <SignContract
                                solution={getSolution()}
                                contractAddress={getSolution()?.escrowSmartContract}
                                userType={userType}
                                onSolutionUpdate={setForceUpdateSolutions}
                            />
                        ) : (
                            ''
                        )
                    }                    
                </div>
            </Fragment>
        )
    }

    const DeployContract = ({ solution }) => {
        return (
            <Fragment>
                {
                    solutionHelper.solution && solutionHelper.solution.escrowSmartContract ? (
                        <div className='row mt-3'>
                                <div className='col-sm-12'>
                                    {
                                        solution.escrowSmartContract.address === null ? (
                                            <p>Contract created !</p>
                                        ) : (
                                            <p>Contract deployed !</p>
                                        )
                                    }
                                </div>
                        </div>                        
                    ) : (
                        <div className='row mt-3'>
                            <div className='col-sm-12'>
                                {
                                    currentSolution.current ? (
                                        <p>Contract created !</p>
                                    ) : (
                                        <p>Created Contract info</p>
                                    )
                                }
                            </div>
                        </div>
                    )
                }
                <EscrowSmartContract
                    solution={solutionManager.solution}
                    loading={solutionManager.loading}
                    error={solutionManager.error}
                />
                {
                    !solution.escrowSmartContract ? (
                        <div className='row mt-1'>
                            {
                                !isContractor ? (
                                    <>
                                        <div className='col-sm-12'>
                                            <Button
                                                className='w-100'
                                                color="dark"
                                                disabled={solutionHelper.loading}
                                                onClick={() => {setShowDeployContractNotice(true)}}
                                            >
                                                Deploy Contract
                                                <EtherLogo style={{ width: '15px' }} className='ms-1' />
                                            </Button>
                                            <div className={solutionHelper.loading ? "upload-spinner d-inline ms-1" : "visually-hidden"}>
                                                <div role="status" className="spinner-border spinner-border-sm text-success align-middle">
                                                    <span className="sr-only"></span>
                                                </div>
                                            </div>
                                            <label className='mt-1' style={{fontFamily: 'inter'}}>
                                                You will have an opportunity to cancel in the next step.
                                            </label>
                                        </div>
                                        <ActionConfirm
                                            key={'deployConfirm'}
                                            visible={showDeployContractNotice}
                                            message={
                                                'The Builde App deploys the Smart Contract to the Sepolia TestNet Blockchain, ' +
                                                'allowing anyone to verify its details by clicking the “Etherscan Link” button. ' +
                                                'This process uses Sepolia Ether from the Builde App wallet, and neither the ' +
                                                'Homeowner nor the Contractor incurs any Sepolia Ether costs for deployment.'
                                            }
                                            onConfirm={() => {deployContract()}}
                                            onClose={() => {setShowDeployContractNotice(false)}}
                                        />
                                    </>
                                ) : (
                                    <div className='col-sm-12'>
                                        <p className='d-inline'>
                                            <span className={'text-success'}>● </span>
                                            Awaiting for Homeowner to deploy contract to Blockchain.
                                        </p>
                                    </div>
                                )
                            }
                            {
                                solutionHelper.error && solutionHelper.error.message ? (
                                    <div className='col-sm-12'>
                                        <FormToast show={true} message={solutionHelper.error.message} className="bg-danger" />
                                    </div>
                                ) : (
                                    ''
                                )
                            }
                        </div>
                    ) : (
                        <ContractView solution={ solution } />
                    )
                }
                
            </Fragment>
        );
    }

    const ModalContent = () => {
        return (
            modalData ?
                <Fragment>
                    <div className='row'>
                        <div className='col-sm-12'>
                            <span className='fw-bold fs-3'>
                                {modalData.projectDescription}
                            </span>
                        </div>
                    </div>
                    <div className='row mt-3'>
                        <div className='col-sm-2 col-3 ms-0'>
                            <img
                                style={{ maxWidth: '50px', width: 'auto', height: 'auto' }}
                                src={spaceAssets[modalData.projectType.description] !== undefined
                                        ? spaceAssets[modalData.projectType.description]
                                        : Living
                                    }
                                alt={modalData.projectType.description}
                            />
                        </div>
                        <div className='col-sm-10 col-9 d-flex align-items-center'>
                            <span className='fw-bold ms-0'>{`${modalData.projectType?.description} Project`}</span>
                        </div>
                    </div>
                    <div className='row mt-2'>
                        <div className='col-sm-6'>
                            <span>Address</span>
                        </div>
                        <div className='col-sm-6'>
                            <span className='fw-bold'>{modalData.address}</span>
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-sm-6'>
                            <span>Postcode</span>
                        </div>
                        <div className='col-sm-6'>
                            <span className='fw-bold'>{modalData.postcode}</span>
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-sm-6'>
                            <span>Administrative Area</span>
                        </div>
                        <div className='col-sm-6'>
                            <span className='fw-bold'>{modalData.administrativeArea}</span>
                        </div>
                    </div>
                    <div className='row mt-3'>
                        <div className='col-sm-12'>Materials</div>
                    </div>
                    <div className='row'>
                        {
                            modalData.materials?.map((materialDetail) => {
                                return (
                                    <Fragment key={materialDetail.id}>
                                        <div className='col-sm-12'>
                                            <div className='row mt-1' style={{background: '#e4f2f7'}}>
                                                <div className='col-lg-2 col-2'>
                                                    <img
                                                        className='material-thumbnail'
                                                        style={{ maxWidth: '50px', width: 'auto', height: 'auto' }}
                                                        src={`/${APIConstants.URLS.MATERIAL.BASE}/${materialDetail.material?.id}/thumbnail`}
                                                        alt={materialDetail.material?.name}
                                                        onError={(e) => { e.target.src = ThumbnailNotAvailable }}
                                                    />
                                                </div>
                                                <div className='col-lg-4 col-6 d-flex align-items-center'>
                                                    <div className='ms-0'>
                                                        <span>
                                                            {`(${materialDetail.quantity}) x `}
                                                        </span>
                                                        <span className='fw-bold'>
                                                            {materialDetail.material?.name}
                                                        </span>
                                                        <span>
                                                            {` ${materialDetail.materialCategory.description} quality`}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className='col-sm-6 col-4 d-flex align-items-center'>
                                                    <span className='fw-bold ms-0'>
                                                        {`${modalData.currency.code} ${(materialDetail.price * materialDetail.quantity).toFixed(2)}`}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </Fragment>
                                )
                            })
                        }
                    </div>
                    <div className='row mt-2'>
                        <div className='col-sm-6'>Total material budget</div>
                        <div className='col-sm-6'>
                            <span className='fw-bold'>
                                {`${modalData.currency.code} ${modalData.materials?.reduce(
                                    (accumulator, current) => accumulator + (current.price * current.quantity),0).toFixed(2)}`
                                }
                            </span>
                        </div>
                    </div>
                    <div className='row mt-1'>
                        <div className='col-sm-6'>Total labor budget</div>
                        <div className='col-sm-6'>
                            <span className='fw-bold'>
                                {`${modalData.currency.code} ${modalData.laborBudget}`}
                            </span>
                        </div>
                    </div>
                    <div className='row mt-3'>
                        <div className='col-sm-6'>
                            <span>Total Project Budget</span>
                        </div>
                        <div className='col-sm-6'>
                            <span className='fw-bold'>{`${modalData.currency.code} ${modalData.totalBudget?.toFixed(2)}`}</span>
                        </div>
                    </div>
                    {
                        modalData.mediaFiles && modalData.mediaFiles.length &&
                        <div className='row mt-3'>
                            {
                                modalData.mediaFiles.map((mediaFile, index) => (
                                    <div key={index} className='col-sm-6 col-6 mb-3'>
                                        <img
                                            src={blobStorage.getUploadedFileUrl(mediaFile.fileName)}
                                            className="img-fluid"
                                            role='button'
                                            alt={`${mediaFile.description}`}
                                            onError={(e) => { e.target.src = ThumbnailNotAvailable }}
                                            onClick={(e) => { setImagePreview(blobStorage.getUploadedFileUrl(mediaFile.fileName)); e.preventDefault() }}
                                        />
                                    </div>
                                ))
                            }
                        </div>
                    }
                    <hr />
                    <Contract />
                </Fragment>
                :
                <Fragment>
                    <div className='row'>
                        <h4 className="alert">We couldn't find the project</h4>
                    </div>
                </Fragment>
        )
    }

    const createContract = () => {
        let solutionIdParam = params['*'];
        let solution = getSolution();
        
        if (solution && solution.id) {
            solutionHelper.createEscrowSmartContract(solution.id);
        } else {
            contractCreateErrorRef.current.innerHTML =
                "We couldn't find the solution #" + solutionIdParam + ". Please reload and try again.";
        }
    }

    const deployContract = () => {
        let solution = getSolution();
        if (solution && solution.id) {
            solutionHelper.deployBuildeESC(solution.id);
        }
    }

    return (
        <Fragment>
            <Modal fullscreen={true} className="font-sf-pro-text-normal solution-details-container" size='lg' isOpen={showModal} toggle={toggle} {...args}>
                <ModalHeader toggle={toggle} className="border-bottom-0 me-0">
                </ModalHeader>
                <ModalBody>
                    <ModalContent />
                </ModalBody>
                <ModalFooter>
                    <Button className='visually-hidden' color="primary" onClick={toggle}>
                        Do Something
                    </Button>{' '}
                    <Button color="secondary" onClick={toggle}>
                        &#8592; Back
                    </Button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={imagePreview !== null} toggle={() => toggleImagePreview(null)} size="lg" autoFocus={false} centered>
                <ModalHeader toggle={() => toggleImagePreview(null)} className="border-bottom-0 me-0"/>
                <ModalBody className="text-center pt-0">
                    {
                        (imagePreview !== null) &&
                        <img
                            src={imagePreview}
                            alt="prevew"
                            className='mw-100 h-auto'
                        />
                    }
                </ModalBody>
            </Modal>
        </Fragment>
    );
}

export default SolutionDetails;